import React, { useMemo, useState } from "react";
import DataGrid from "../../../widgets/DataGrid/DataGrid";
import { useCustomTranslation } from "../../../hooks/useCustomTranslation";
import CustomTextField from "../../../widgets/CustomTextField/CustomTextField";
import AmountRangeFilter from "../../../widgets/AmountRangeFilter/AmountRangeFilter";
import { matchesAmountRange, matchesText } from "../../../DataTableUtils/DataTableUtils";

const CampaignsSummaryTable = ({ rows, loading, onSelect }) => {
  const { t } = useCustomTranslation("Promises List");
  const [tableFilter, setTableFilter] = useState({
    campaign_name: "",
    count: "",
    promises_total_min: "",
    promises_total_max: "",
    donations_received_min: "",
    donations_received_max: "",
    uncollected_min: "",
    uncollected_max: "",
  });

  const changeTableFilter = (option, value) => {
    setTableFilter((prev) => ({ ...prev, [option]: value }));
  };

  const clearTableFilter = (option) => {
    setTableFilter((prev) => ({ ...prev, [option]: "" }));
  };

  const columns = useMemo(() => [
    { field: "campaign_name", headerName: t("campaign"), sortable: true },
    { field: "count", headerName: t("fundraisers"), sortable: true, numberSort: true },
    { field: "promises_total", headerName: t("promises"), sortable: true, numberSort: true, sortField: "promises_total_value" },
    { field: "donations_received", headerName: t("donations_received"), sortable: true, numberSort: true, sortField: "donations_received_value" },
    { field: "uncollected", headerName: t("uncollected_promises"), sortable: true, numberSort: true, sortField: "uncollected_value" },
  ], [t]);

  const filters = {
    campaign_name: (
      <CustomTextField
        value={tableFilter.campaign_name || ""}
        onChange={(e) => changeTableFilter("campaign_name", e.target.value)}
        clearFilter={() => clearTableFilter("campaign_name")}
      />
    ),
    count: (
      <CustomTextField
        value={tableFilter.count || ""}
        onChange={(e) => changeTableFilter("count", e.target.value)}
        clearFilter={() => clearTableFilter("count")}
      />
    ),
    promises_total: (
      <AmountRangeFilter
        min={tableFilter.promises_total_min}
        max={tableFilter.promises_total_max}
        onMinChange={(e) => changeTableFilter("promises_total_min", e.target.value)}
        onMaxChange={(e) => changeTableFilter("promises_total_max", e.target.value)}
        onClearMin={() => clearTableFilter("promises_total_min")}
        onClearMax={() => clearTableFilter("promises_total_max")}
        t={t}
      />
    ),
    donations_received: (
      <AmountRangeFilter
        min={tableFilter.donations_received_min}
        max={tableFilter.donations_received_max}
        onMinChange={(e) => changeTableFilter("donations_received_min", e.target.value)}
        onMaxChange={(e) => changeTableFilter("donations_received_max", e.target.value)}
        onClearMin={() => clearTableFilter("donations_received_min")}
        onClearMax={() => clearTableFilter("donations_received_max")}
        t={t}
      />
    ),
    uncollected: (
      <AmountRangeFilter
        min={tableFilter.uncollected_min}
        max={tableFilter.uncollected_max}
        onMinChange={(e) => changeTableFilter("uncollected_min", e.target.value)}
        onMaxChange={(e) => changeTableFilter("uncollected_max", e.target.value)}
        onClearMin={() => clearTableFilter("uncollected_min")}
        onClearMax={() => clearTableFilter("uncollected_max")}
        t={t}
      />
    ),
  };

  const filteredRows = useMemo(
    () =>
      rows.filter(
        (row) =>
          matchesText(row.campaign_name, tableFilter.campaign_name) &&
          matchesText(row.count, tableFilter.count) &&
          matchesAmountRange(
            row.promises_total_value,
            tableFilter.promises_total_min,
            tableFilter.promises_total_max
          ) &&
          matchesAmountRange(
            row.donations_received_value,
            tableFilter.donations_received_min,
            tableFilter.donations_received_max
          ) &&
          matchesAmountRange(
            row.uncollected_value,
            tableFilter.uncollected_min,
            tableFilter.uncollected_max
          )
      ),
    [rows, tableFilter]
  );

  const handleSelect = (e) => {
    const row = rows.find((r) => r.id === e.id);
    if (row) onSelect({ id: row.campaign_id, name: row.campaign_name });
  };

  return (
    <div className="container-white">
      <DataGrid
        columns={columns}
        rows={filteredRows}
        filters={filters}
        loading={loading}
        showDetails={handleSelect}
      />
    </div>
  );
};

export default CampaignsSummaryTable;
